import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import Lenis from 'lenis';
import { setLenisInstance } from '../../utils/scrollUtils';

interface ScrollToOptions {
  offset?: number;
  immediate?: boolean;
  duration?: number;
  onComplete?: () => void;
}

interface LenisContextValue {
  lenis: Lenis | null;
  scrollTo: (target: string | HTMLElement | number, options?: ScrollToOptions) => void;
}

// Create context for Lenis instance
const LenisContext = createContext<LenisContextValue>({
  lenis: null,
  scrollTo: () => {},
});

export const useLenis = () => useContext(LenisContext);

interface LenisProviderProps {
  children: ReactNode;
}

export const LenisProvider: React.FC<LenisProviderProps> = ({ children }) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // Initialize Lenis with custom settings
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      // Important for your site's header offset
      scrollPadding: { top: 80 } as { top: number }
    });

    // Store the instance in ref for access throughout the app
    lenisRef.current = lenis;

    // Set the Lenis instance in the scroll utilities
    setLenisInstance(lenis);

    // RAF loop for Lenis
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Cleanup
    return () => {
      lenis.destroy();
      setLenisInstance(null);
    };
  }, []);

  // Helper function to scroll to elements
  const scrollTo = (
    target: string | HTMLElement | number,
    options: ScrollToOptions = {}
  ): void => {
    if (!lenisRef.current) return;

    const defaultOptions: ScrollToOptions = {
      offset: -80, // Your header height
      immediate: false,
      duration: 1.2,
      ...options
    };

    if (typeof target === 'string') {
      const element = document.getElementById(target);
      if (element) {
        lenisRef.current.scrollTo(element, defaultOptions);
      }
    } else if (target instanceof HTMLElement) {
      lenisRef.current.scrollTo(target, defaultOptions);
    } else if (typeof target === 'number') {
      lenisRef.current.scrollTo(target, defaultOptions);
    }
  };

  return (
    <LenisContext.Provider value={{ lenis: lenisRef.current, scrollTo }}>
      {children}
    </LenisContext.Provider>
  );
};
